/**
 * Invoice contracts — mirrors `platform.invoices`.
 *
 * Invoices are billing documents tied to a tenant subscription.
 * Invoice numbers are auto-generated as INV-{YYYY}-{5-digit}.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enum schemas
// ---------------------------------------------------------------------------

export const InvoiceStatusSchema = z.enum(['draft', 'sent', 'paid', 'overdue', 'void']);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

// ---------------------------------------------------------------------------
// Record (output)
// ---------------------------------------------------------------------------

export const InvoiceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  invoiceNumber: z.string(),
  amount: z.string(), // numeric as string from DB
  currency: z.string().length(3),
  status: InvoiceStatusSchema,
  dueDate: z.string(), // YYYY-MM-DD date string
  paidAt: z.string().datetime({ offset: true }).nullable(),
  paymentReference: z.string().nullable(),
  lineItems: z.unknown().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

// ---------------------------------------------------------------------------
// Create input
// ---------------------------------------------------------------------------

export const CreateInvoiceInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  amount: z.number().min(0),
  currency: z.string().length(3).default('INR'),
  status: InvoiceStatusSchema.default('draft'),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  lineItems: z.array(z.record(z.unknown())).optional(),
});
export type CreateInvoiceInput = z.infer<typeof CreateInvoiceInputSchema>;

// ---------------------------------------------------------------------------
// Mark invoice paid input
// ---------------------------------------------------------------------------

export const MarkInvoicePaidInputSchema = z.object({
  paymentReference: z.string().min(1),
  paidAt: z.string().datetime({ offset: true }).optional(),
});
export type MarkInvoicePaidInput = z.infer<typeof MarkInvoicePaidInputSchema>;
